import { useState, useEffect, useCallback, RefObject } from 'react';

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
  mozCancelFullScreen?: () => void;
}

interface FullscreenHTMLElement extends HTMLElement {
  mozRequestFullScreen?: () => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void>;
}

interface IOSVideoElement extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
}

function getFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? doc.mozFullScreenElement ?? null;
}

async function requestFullscreen(element: HTMLElement): Promise<boolean> {
  const elem = element as FullscreenHTMLElement;
  try {
    if (elem.requestFullscreen) {
      await elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      await elem.webkitRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      await elem.mozRequestFullScreen();
    } else {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

async function exitFullscreen(): Promise<void> {
  const doc = document as FullscreenDocument;
  try {
    if (doc.exitFullscreen) {
      await doc.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    } else if (doc.mozCancelFullScreen) {
      doc.mozCancelFullScreen();
    }
  } catch {
    // Silently handle "Document not active" and similar browser errors
  }
}

export function useFullscreen(
  containerRef: RefObject<HTMLElement | null>,
  iosVideoRef?: RefObject<HTMLVideoElement | null>,
) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => {
      const fsElement = getFullscreenElement();
      setIsFullscreen(fsElement != null && fsElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('mozfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('mozfullscreenchange', onChange);
    };
  }, [containerRef]);

  const enter = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;

    const ok = await requestFullscreen(container);
    if (!ok && iosVideoRef?.current) {
      (iosVideoRef.current as IOSVideoElement).webkitEnterFullscreen?.();
      setIsFullscreen(true);
    }
  }, [containerRef, iosVideoRef]);

  const exit = useCallback(async () => {
    if (getFullscreenElement()) {
      await exitFullscreen();
    } else if (iosVideoRef?.current) {
      (iosVideoRef.current as IOSVideoElement).webkitExitFullscreen?.();
      setIsFullscreen(false);
    }
  }, [iosVideoRef]);

  const toggle = useCallback(() => {
    if (isFullscreen) {
      exit();
    } else {
      enter();
    }
  }, [isFullscreen, enter, exit]);

  return { isFullscreen, enter, exit, toggle };
}
